import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { Field, Formik, Form } from 'formik';
import * as Yup from 'yup';
import { PATH_CLIENT_EDIT } from 'routing/path'
import PageHeader from 'components/page-header'
import ValidatedField from 'components/controls/validated-field';
import ValidatedMaskedField from 'components/controls/validated-masked-field';
import ValidatedSelectField from 'components/controls/validated-select-field';
import { showSuccessNotification, showErrorNotification } from "actions/action-complete-notificator";
import { useGetDosemeterQuery, useSaveDosemeterMutation } from 'actions/client/client-dosemeter-measurement-api';
import { useGetFacilitiesQuery } from 'actions/client/client-facility-api';
import { translationApi  } from 'actions/translation-api';
import { getDosemeterTypeDescription, getDosemeterTypeCode, DOSEMETER_TYPE_ENVIRONMENTAL, DOSEMETER_TYPE_PERSONAL, DOSEMETER_TYPE_PERSONAL_WRIST } from 'data/enums/dosemeter-type-enum';
import { tryParseInt } from 'utils/number-utils';

const ClientDosemeterEdit = () => {
	const navigate = useNavigate();
	const params = useParams();
	const clientDosemeterIdString = params.clientDosemeterId;
	const clientDosemeterId = tryParseInt(clientDosemeterIdString);

	const { data: dosemeter, isLoading: loadDosemeterInProgress, isFetching: fetchDosemeterInProgress } = useGetDosemeterQuery(clientDosemeterId, clientDosemeterId ? { refetchOnMountOrArgChange: true } : { skip: true } );
	const dosemeterReady = !loadDosemeterInProgress && !fetchDosemeterInProgress && dosemeter;
	
	const clientFacilitiesRequest = {
		clientId: dosemeter?.clientId,
		page: 1,
		pageSize: 1000
	};
	const { data: clientFacilities, isLoading: loadClientFacilitiesInProgress, isFetching: fetchClientFacilitiesInProgress } = useGetFacilitiesQuery(clientFacilitiesRequest, !dosemeterReady ? { skip: true } : { refetchOnMountOrArgChange: true } );
	const clientFacilitiesReady = !loadClientFacilitiesInProgress && !fetchClientFacilitiesInProgress && clientFacilities;
	
	const [ saveDosemeter, { isLoading: saveDosemeterInProgress }] = useSaveDosemeterMutation();
	const translations = useSelector(translationApi.endpoints.getTranslations.select()).data ?? {};

	const getPageHeader = () => {
		// existing client dosemeter pending
		if (loadDosemeterInProgress || fetchDosemeterInProgress) {
			return <PageHeader title="..." breadcrumbs={[]} breadcrumbsCurrentPage="..." />
		}

		const breadcrumbs = [
			{ name: `${translations.client} ${dosemeter?.clientName}`, link: PATH_CLIENT_EDIT + dosemeter?.clientId },
		];
		let title = `${translations.dosemeter} ${dosemeter?.clientId?.toString().padStart(4, '0')}-${getDosemeterTypeCode(dosemeter.dosemeterType)}${dosemeter?.clientDosemeterNumber}`;
		if (dosemeter?.clientDosemeterOldNumber) {
			title = `${title} (daw. ${dosemeter?.clientId?.toString().padStart(4, '0')}-${dosemeter?.clientDosemeterOldNumber})`;
		}
		return <PageHeader title={title} breadcrumbs={breadcrumbs} breadcrumbsCurrentPage={title} />
	}

	const initializeDosemeter = dosemeterReady && clientFacilitiesReady;
	const initialValues = {
		clientId: initializeDosemeter ? dosemeter?.clientId?.toString().padStart(4, '0') : "",
		dosemeterType: initializeDosemeter ? dosemeter.dosemeterType : "",
		number: initializeDosemeter ? dosemeter.clientDosemeterNumber : "",
		clientFacility: initializeDosemeter ? dosemeter.clientFacilityId : "",
		firstName:  initializeDosemeter ? dosemeter.firstName : "",
		lastName: initializeDosemeter ? dosemeter.lastName : "",
	};
	
	var clientFacilitiesToDropDownList = clientFacilitiesReady ?
		clientFacilities.clientFacilities.map(function (clientFacility) {
			return [ clientFacility.clientFacilityId, clientFacility.name ]
		}) : null;

	let fourDigitNumberRegex = /^\d{4}$/;
	let fourDigitNumberInputMask = ['0', '0', '0', '0'];
	let dosemeterTypesToDropDownList = dosemeterReady ? [
		[dosemeter.dosemeterType, getDosemeterTypeDescription(dosemeter.dosemeterType, translations)]
	] : null;
	
	const validationSchema = Yup.object().shape({
		clientId: Yup.string()
			.required(translations.warningFieldRequired)
			.matches(fourDigitNumberRegex, translations.warningValueInvalid),
		dosemeterType: Yup.number()
			.required(translations.warningFieldRequired),
		number: Yup.string()
			.required(translations.warningFieldRequired)
			.matches(fourDigitNumberRegex, translations.warningValueInvalid),
		firstName: Yup.string()
			.when("dosemeterType", {
				is: dosemeterType => dosemeterType == DOSEMETER_TYPE_PERSONAL || dosemeterType == DOSEMETER_TYPE_PERSONAL_WRIST,
				then: () => Yup.string().required(translations.warningFieldRequired)
			}),
		lastName: Yup.string()
			.when("dosemeterType", {
				is: dosemeterType => dosemeterType == DOSEMETER_TYPE_PERSONAL || dosemeterType == DOSEMETER_TYPE_PERSONAL_WRIST,
				then: () => Yup.string().required(translations.warningFieldRequired)
			}),
	});
	
	const submitDisabled = !dosemeterReady || dosemeter.dosemeterType === DOSEMETER_TYPE_ENVIRONMENTAL || !clientFacilitiesReady || saveDosemeterInProgress;
	const handleSubmit = async (fields) => {
		const dosemeterToUpdate = {
			dosemeterId: clientDosemeterId,
			clientId: dosemeter.clientId,
			clientFacilityId: tryParseInt(fields.clientFacility),
			clientDosemeterNumber: dosemeter.clientDosemeterNumber,
			firstMeasurementPeriodStart: dosemeter.measurementPeriodStart,
			firstMeasurementPeriodEnd: dosemeter.measurementPeriodEnd,
            dosemeterType: tryParseInt(fields.dosemeterType),
			firstName: fields.firstName,
			lastName: fields.lastName
		};

		const result = await saveDosemeter(dosemeterToUpdate);
		if ('error' in result) {
			showErrorNotification(result.error);
		}
		else {
			showSuccessNotification(translations.changesSaved);
		}
	}

	const randomAutoComplete = Math.random().toString(36).replace(/[^a-z]+/g, "").substr(0, 5);
	return (
		<div>
			{ getPageHeader()}
			{ <Formik
				enableReinitialize
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={ async (fields) => { await handleSubmit(fields) }}>
				{props => (
					<Form autoComplete={randomAutoComplete}>
					<div className="col-12 mb-4">
						<div className="card border-light shadow-sm components-section">
							<div className="card-body">
								<h2 className="h5 mb-4">{translations.basicData}</h2>
								<div className="row mb-4">
									{dosemeterReady &&
										<div className="col-lg-4 col-sm-6">
											<ValidatedMaskedField name="clientId" mask={fourDigitNumberInputMask} label={translations.clientNumber} readOnly={true} {...props} />
										</div>
									}
									{dosemeterReady &&
										<div className="col-lg-4 col-sm-6">
											<ValidatedSelectField name="dosemeterType" label={translations.type} keyValueMap={dosemeterTypesToDropDownList} readOnly={true} {...props} />
										</div>
									}
									{dosemeterReady &&
										<div className="col-lg-4 col-sm-6">
											<ValidatedMaskedField name="number" mask={fourDigitNumberInputMask} label={translations.number} readOnly={true} {...props} />
										</div>
									}
									{clientFacilitiesToDropDownList && clientFacilitiesToDropDownList.length > 0 &&
										<div className="col-lg-4 col-sm-6">
											<ValidatedSelectField name="clientFacility" label={translations.clientFacility} keyValueMap={clientFacilitiesToDropDownList} {...props} />
										</div>
									}
								</div>
								{dosemeterReady && (dosemeter.dosemeterType === DOSEMETER_TYPE_PERSONAL || dosemeter.dosemeterType === DOSEMETER_TYPE_PERSONAL_WRIST) &&
									<div className="row mb-4">
										<Field name="dosemeterType" type="hidden" /> 
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="firstName" label={translations.firstName} {...props} />
										</div>
										<div className="col-lg-4 col-sm-6">
											<ValidatedField name="lastName" label={translations.lastName} {...props} />
										</div>
									</div>
								}
								<div className="row mb-5 mb-lg-5">
									<div className="col-lg-3 col-md-6">
										<div className="form-group mb-3">
											<button id="saveButton" type="submit" className="btn btn-primary me-2" disabled={submitDisabled ? "disabled" : null} >{translations.actionSave}</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Form>)}
			</Formik>}
		</div>
	)
} 

export default ClientDosemeterEdit;
